<template>
	<div class="anamnesis">
		<v-tabs
			vertical
			left
			color="primary"
			slider-color="primary"
			background-color="secondary"
			active-class="primary--text"
			style="border-radius: 4px"
			v-model="tabOpen"
		>
			<v-tab class="text-caption" v-translate>Chief Complaint</v-tab>
			<v-tab class="text-caption" v-translate>History of the present illness</v-tab>
			<v-tab class="text-caption" v-translate>Past medical history</v-tab>
			<v-tab class="text-caption" v-translate>Family history</v-tab>
			<v-tab class="text-caption" v-translate>Life habits</v-tab>
			<v-tab class="text-caption" v-translate>Review of Systems</v-tab>
			<v-tab class="text-caption" v-translate>Comparative assessment</v-tab>
			<v-tab class="text-caption" v-translate>Overview</v-tab>
			<v-divider></v-divider>
			<v-tab class="text-caption" v-translate>Drugs</v-tab>
			<v-tab class="text-caption" v-translate>Documents</v-tab>
			<v-divider></v-divider>
			<v-tab class="text-caption" v-translate>Observations</v-tab>
			<div style="height: 5px; background-color: white">
			</div>
			<v-btn class="text-caption primary--text" :disabled="cantEdit" text @click="getAppointments" :loading="loadingAppointmentsToImport">
				<v-icon class="mr-1" small>mdi-calendar-import</v-icon>
				<translate>History</translate>
				<v-dialog v-model="showAppointmentsToImport" max-width="50%" scrollable>
					<v-card>
						<v-card-title class="title primary--text mb-2 secondary">
							<translate>Import Anamnesis</translate>
						</v-card-title>
						<v-card-text class="pt-2">
							<p
								v-if="appointmentsToImport.length == 0"
								class="font-weight-thin ml-2"
								v-translate
							>No appointments.</p>
							<v-card v-else class="mx-auto mb-2" v-for="appointment in appointmentsToImport" :key="appointment.id">
								<v-list-item three-line @click="importAppointment(appointment.id, 'all')">
									<v-list-item-content>
										<v-list-item-title class="primary--text">
											<v-row no-gutters class="text-body-1">
												<v-icon class="mr-1" small color="primary">mdi-account-tie</v-icon>
													<span class="mr-2">{{appointment.user.name}}</span>
												<v-icon class="mr-1" small color="primary">mdi-certificate</v-icon>
												<span>{{appointment.speciality.name}}</span>
												<v-spacer></v-spacer>
												<v-icon class="mr-1" small color="primary">mdi-calendar</v-icon>
													<span class="mr-2">{{appointment.date}}</span>
												<v-icon class="mr-1" small color="primary">mdi-clock-outline</v-icon>
												<span>{{appointment.time}}</span>
											</v-row>
										</v-list-item-title>
										<v-list-item-subtitle>{{appointment.description}}</v-list-item-subtitle>
										<ClinicHistoryAnamnesis
											:appointment="appointment"
											:importOption="true"
											:documents="documents"
											class="mt-3"
											@import-topic="importAppointment"
										/>
									</v-list-item-content>
								</v-list-item>
							</v-card>
						</v-card-text>
						<v-divider></v-divider>
						<v-card-actions>
							<v-spacer></v-spacer>
							<v-btn color="primary darken-1" text @click="showAppointmentsToImport = false">
								<translate>Close</translate>
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</v-btn>
			<v-row style="background-color: white" no-gutters>
			</v-row>
			<v-tab-item key="chief_complaint" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('Chief Complaint')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.chief_complaint"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
					<SearchICD
						:selectedICD="chiefComplaintICD"
						@add-icd="addICD"
						@remove-icd="removeICD"
						type="chief_complaint"
						:patient="appointment.patient"
						@open-send-monitoring-email="showSendMonitoringEmail = true"
						:disabled="cantEdit"
					/>
				</v-container>
			</v-tab-item>
			<v-tab-item key="history_illness" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('History of the present illness')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.history_illness"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
					<SearchICD
						:selectedICD="presentIllnessICD"
						@add-icd="addICD"
						@remove-icd="removeICD"
						type="history_illness"
						:patient="appointment.patient"
						@open-send-monitoring-email="showSendMonitoringEmail = true"
						:disabled="cantEdit"
					/>
				</v-container>
			</v-tab-item>
			<v-tab-item key="past_medical" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('Past medical history')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.past_medical"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
					<SearchICD
						:selectedICD="medicalHistoryICD"
						@add-icd="addICD"
						@remove-icd="removeICD"
						type="past_medical"
						:patient="appointment.patient"
						@open-send-monitoring-email="showSendMonitoringEmail = true"
						:disabled="cantEdit"
					/>
				</v-container>
			</v-tab-item>
			<v-tab-item key="family_history" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('Family history')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.family_history"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
					<SearchICD
						:selectedICD="familyHistoryICD"
						@add-icd="addICD"
						@remove-icd="removeICD"
						type="family_history"
						:patient="appointment.patient"
						@open-send-monitoring-email="showSendMonitoringEmail = true"
						:disabled="cantEdit"
					/>
				</v-container>
			</v-tab-item>
			<v-tab-item key="life_habits" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('Life habits')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.life_habits"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
					<SearchICD
						:selectedICD="lifeHabitsICD"
						@add-icd="addICD"
						@remove-icd="removeICD"
						type="life_habits"
						:patient="appointment.patient"
						@open-send-monitoring-email="showSendMonitoringEmail = true"
						:disabled="cantEdit"
					/>
				</v-container>
			</v-tab-item>
			<v-tab-item key="review_systems" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('Review of Systems')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.review_systems"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
					<SearchICD
						:selectedICD="reviewSystemsICD"
						@add-icd="addICD"
						@remove-icd="removeICD"
						type="review_systems"
						:patient="appointment.patient"
						@open-send-monitoring-email="showSendMonitoringEmail = true"
						:disabled="cantEdit"
					/>
				</v-container>
			</v-tab-item>
			<v-tab-item key="comparative_assessment" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('Comparative assessment')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.comparative_assessment"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
					<SearchICD
						:selectedICD="compAssessmentICD"
						@add-icd="addICD"
						@remove-icd="removeICD"
						type="comparative_assessment"
						:patient="appointment.patient"
						@open-send-monitoring-email="showSendMonitoringEmail = true"
						:disabled="cantEdit"
					/>
				</v-container>
			</v-tab-item>
			<v-tab-item key="overview" :transition="false" :reverse-transition="false">
				<v-container>
					<h3 class="primary--text" v-translate>Chief Complaint</h3>
					<p class="grey--text my-2 pl-2 text-body-2">{{appointment.chief_complaint}}</p>
					<ICDList :list="chiefComplaintICD" :editable="false" />
					<h3 class="primary--text" v-translate>History of the Present Illness</h3>
					<p class="grey--text my-2 pl-2 text-body-2">{{appointment.history_illness}}</p>
					<ICDList :list="presentIllnessICD" :editable="false" />
					<h3 class="primary--text" v-translate>Past Medical History</h3>
					<p class="grey--text my-2 pl-2 text-body-2">{{appointment.past_medical}}</p>
					<ICDList :list="medicalHistoryICD" :editable="false" />
					<h3 class="primary--text" v-translate>Family History</h3>
					<p class="grey--text my-2 pl-2 text-body-2">{{appointment.family_history}}</p>
					<ICDList :list="familyHistoryICD" :editable="false" />
					<h3 class="primary--text" v-translate>Life Habits</h3>
					<p class="grey--text my-2 pl-2 text-body-2">{{appointment.life_habits}}</p>
					<ICDList :list="lifeHabitsICD" :editable="false" />
					<h3 class="primary--text" v-translate>Comparative Assessment</h3>
					<p class="grey--text my-2 pl-2 text-body-2">{{appointment.comparative_assessment}}</p>
					<ICDList :list="compAssessmentICD" :editable="false" />
					<h3 class="primary--text" v-translate>Review of Systems</h3>
					<p class="grey--text my-2 pl-2 text-body-2">{{appointment.review_systems}}</p>
					<ICDList :list="reviewSystemsICD" :editable="false" />
				</v-container>
			</v-tab-item>
			<v-tab-item key="drugs" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('Drugs')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.drug_description"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
					<SearchDrugs
						:selectedDrugs="drugs"
						context="appointment"
						@import-drugs="importAppointmentDrugs"
						:disabled="cantEdit"
					/>
				</v-container>
			</v-tab-item>
			<v-tab-item key="documents" :transition="false" :reverse-transition="false">
				<Documents :documents="documents" :disabled="cantEdit" />
			</v-tab-item>
			<v-tab-item key="observations" :transition="false" :reverse-transition="false">
				<v-container>
					<v-textarea
						filled
						class="text-body-2"
						:label="$gettext('Observations')"
						:placeholder="$gettext('Notes')"
						v-model="appointment.observations"
						@blur="editAppointment"
						:disabled="cantEdit"
						auto-grow
						persistent-placeholder
					></v-textarea>
				</v-container>
			</v-tab-item>
		</v-tabs>
		<SendMonitoringEmail
			:show="showSendMonitoringEmail"
			:patient="appointment.patient"
			@close="showSendMonitoringEmail = false"
		/>
	</div>
</template>

<script>
import axios from "axios";
import SearchICD from "@/components/icd/SearchICD.vue";
import ICDList from "@/components/icd/ICDList.vue";
import SearchDrugs from "@/components/drugs/SearchDrugs.vue";
import SendMonitoringEmail from "@/components/patient/SendMonitoringEmail.vue";
import Documents from "@/components/appointment/anamnesis/Documents.vue";
import { mapGetters } from 'vuex'
import ClinicHistoryAnamnesis from '@/components/patient/ClinicHistoryAnamnesis.vue';

export default {
	name: "Anamnesis",
	props: ["appointment", "drugs", "icds", "documents"],
	components: {
		SearchICD,
		ICDList,
		SearchDrugs,
		SendMonitoringEmail,
		Documents,
		ClinicHistoryAnamnesis
	},
	data: () => ({
		loadingAppointmentsToImport: false,
		appointmentsToImport: [],
		showAppointmentsToImport: false,
		showSendMonitoringEmail: false,
		tabOpen: null
	}),
	computed: {
		chiefComplaintICD() {
			return this.icds.filter(s => s.type == "chief_complaint");
		},
		presentIllnessICD() {
			return this.icds.filter(s => s.type == "history_illness");
		},
		medicalHistoryICD() {
			return this.icds.filter(s => s.type == "past_medical");
		},
		familyHistoryICD() {
			return this.icds.filter(s => s.type == "family_history");
		},
		lifeHabitsICD() {
			return this.icds.filter(s => s.type == "life_habits");
		},
		compAssessmentICD() {
			return this.icds.filter(s => s.type == "comparative_assessment");
		},
		reviewSystemsICD() {
			return this.icds.filter(s => s.type == "review_systems");
		},
		cantEdit() {
			return !this.$store.getters.hasAnyModule;
		},
		...mapGetters({
      tourStep: 'tourStepEvent'
    })
	},
	methods: {
		removeICD(icd) {
			if (icd) {
				console.log(icd.code);
				this.icds.splice(this.icds.indexOf(icd), 1);
			}
		},
		addICD(icd) {
			if (icd) {
				this.icds.push(icd);
			}
		},
		editAppointment() {
			const patientId = this.$route.params.pid;
			const formData = new FormData();
			if (this.appointment.chief_complaint != null) {
				formData.append("chief_complaint", this.appointment.chief_complaint);
			}
			if (this.appointment.history_illness != null) {
				formData.append("history_illness", this.appointment.history_illness);
			}
			if (this.appointment.past_medical != null) {
				formData.append("past_medical", this.appointment.past_medical);
			}
			if (this.appointment.family_history != null) {
				formData.append("family_history", this.appointment.family_history);
			}
			if (this.appointment.life_habits != null) {
				formData.append("life_habits", this.appointment.life_habits);
			}
			if (this.appointment.comparative_assessment != null) {
				formData.append(
					"comparative_assessment",
					this.appointment.comparative_assessment
				);
			}
			if (this.appointment.review_systems != null) {
				formData.append("review_systems", this.appointment.review_systems);
			}
			if (this.appointment.observations != null) {
				formData.append("observations", this.appointment.observations);
			}
			if (this.appointment.drug_description != null) {
				formData.append("drug_description", this.appointment.drug_description);
			}
			formData.append("complaint_submit", true);
			axios
				.post(
					"/patient/" + patientId + "/appointment/" + this.appointment.id,
					formData
				)
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						if (response.data.result === "edited") {
							console.log("EDITED");
						}
					}
				})
				.catch(error => console.log(error));
		},
		getAppointments() {
			this.loadingAppointmentsToImport = true;
			const patientId = this.$route.params.pid;
			const appointmentId = this.$route.params.aid;
			axios
				.get(
					"/patient/" +
						patientId +
						"/appointment/" +
						appointmentId +
						"/import_options"
				)
				.then(response => {
					console.log(response.data);
					if (response.status === 200) {
						this.appointmentsToImport = response.data.appointments;
						this.showAppointmentsToImport = true;
					}
					this.loadingAppointmentsToImport = false;
				})
				.catch(error => {
					console.log(error);
					this.loadingAppointmentsToImport = false;
				});
		},
		importAppointment(id, topic) {
			console.log("importing appointment " + id);
			this.$emit("import-anamnese", id, topic);
			this.showAppointmentsToImport = false;
		},
		importAppointmentDrugs(id) {
			this.$emit("import-drugs", id);
		}
	},
	watch: {
		tourStep(value) {
			if(value == 'tour-anamnesis-drugs') {
				this.tabOpen = 8;
			}
		}
	}
};
</script>

<style scoped>
.v-tab {
	justify-content: left !important;
	min-width: 240px;
}
.v-slide-group__wrapper {
	box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.08) !important;
	margin-right: 10px !important;
}
</style>